// ---------------- Axios Auth Setup ----------------
// const AxiosAuthProvider = ({ children }) => {
//   const { getAccessTokenSilently, isAuthenticated, isLoading: auth0Loading } = useAuth0();
//   const [axiosReady, setAxiosReady] = React.useState(false);

//   React.useEffect(() => {
//     if (!isAuthenticated || auth0Loading) return;

//     const setupInterceptor = async () => {
//       const interceptorId = axiosInstance.interceptors.request.use(
//         async (config) => {
//           const token = await getAccessTokenSilently();
//           config.headers = {
//             ...config.headers,
//             Authorization: `Bearer ${token}`,
//           };
//           return config;
//         }
//       );

//       setAxiosReady(true); // interceptor is ready

//       return () => axiosInstance.interceptors.request.eject(interceptorId);
//     };

//     const cleanupPromise = setupInterceptor();
//     return () => {
//       cleanupPromise.then((cleanup) => cleanup && cleanup());
//     };
//   }, [getAccessTokenSilently, isAuthenticated, auth0Loading]);

//   // Only render children when interceptor is ready
//   if (!isAuthenticated || !axiosReady) {
//     return <div>Loading...</div>; // or null/spinner
//   }

//   return children;
// };

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "services/axiosInstance";

import { UserProvider } from "./context/useUserStore";
import { ModulesProvider } from "./context/useModulesStore";
import { SchoolsProvider } from "./context/useSchoolsStore";
import { DepartmentsProvider } from "./context/useDepartmentsStore";
import { PromptCategoriesProvider } from "./context/usePromptCategoriesStore";
import { PromptsProvider } from "./context/usePromptsStore";
import { UserRightsProvider } from "context/useUserRightsStore";
import { ThemeProvider } from "context/useThemeStore";
import { LanguageProvider } from "context/useLanguageStore";
import { WebSocketProvider } from "context/useWebsocketStore";

import LoadingSpinner from "./views/Components/LoadingSpinner";

import OnboardingController from "./views/Components/OnboardingController";

import App from "./app";
import "./css/app.css";

const auth0Domain = "eduprompt.eu.auth0.com";
const auth0ClientId = "ttBGtyEpU42MBvtsxuaFQp0pROHjWIKb";
const auth0Audience = "https://eduprompt.it/api"; //"https://eduprompt.eu.auth0.com/api/v2/";
const auth0RedirectUrl = "https://ittterni.eduprompt.it";
// const auth0RedirectUrl = "http://localhost:3000";

const theme = localStorage.getItem("SA_user_theme") || "light";
document.documentElement.setAttribute("data-theme", theme);

const AuthBootstrap = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [tokenReady, setTokenReady] = React.useState(false);

  React.useEffect(() => {
    if (isLoading) return;

    // If not logged in, we don't need a token, so bootstrap is "ready"
    if (!isAuthenticated) {
      setTokenReady(true);
      return;
    }

    const init = async () => {
      try {
        await getAccessTokenSilently();
        setTokenReady(true);
      } catch (e) {
        console.error("Token bootstrap failed", e);
        // Even on error, set ready so the app can show a login screen or error
        setTokenReady(true);
      }
    };

    init();
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Only show loading during the actual Auth0 "thinking" phase
  if (isLoading || !tokenReady) {
    return <LoadingSpinner />;
  }

  return children;
};

const AxiosProvider = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [interceptorReady, setInterceptorReady] = React.useState(false);
  const interceptorRef = React.useRef(null);

  React.useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      if (interceptorRef.current) {
        axiosInstance.interceptors.request.eject(interceptorRef.current);
        interceptorRef.current = null;
      }
      setInterceptorReady(true); // "Ready" because there's nothing to intercept
      return;
    }

    if (!interceptorRef.current) {
      interceptorRef.current = axiosInstance.interceptors.request.use(async (config) => {
        const token = await getAccessTokenSilently();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
      setInterceptorReady(true);
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  // Only block if we ARE logged in but the interceptor isn't set yet
  if (isAuthenticated && !interceptorReady) {
    return <div>Finalizing Connection...</div>;
  }

  return children;
};

// ---------------- Auth0 Navigate Wrapper ----------------
const Auth0ProviderWithNavigate = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || "/");
  };

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      authorizationParams={{
        redirect_uri: auth0RedirectUrl,
        audience: auth0Audience,
        scope: "openid profile email read:current_user update:current_user_metadata",
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      onRedirectCallback={onRedirectCallback}
    >
      <AuthBootstrap>
        <AxiosProvider>
          <UserProvider>
            <UserRightsProvider>
              <ModulesProvider>
                <SchoolsProvider>
                  <DepartmentsProvider>
                    <PromptCategoriesProvider>
                      <PromptsProvider>
                        <ThemeProvider>
                          <LanguageProvider>
                            <WebSocketProvider>
                              {children}
                              <OnboardingController />
                            </WebSocketProvider>
                          </LanguageProvider>
                        </ThemeProvider>
                      </PromptsProvider>
                    </PromptCategoriesProvider>
                  </DepartmentsProvider>
                </SchoolsProvider>
              </ModulesProvider>
            </UserRightsProvider>
          </UserProvider>
        </AxiosProvider>
      </AuthBootstrap>
    </Auth0Provider>
  );
};

// ---------------- Render ----------------
const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <Router>
    <Auth0ProviderWithNavigate>
      <App />
    </Auth0ProviderWithNavigate>
  </Router>,
);
